import * as THREE from "three";
import { GRAVITY, HARBOUR_WAVES, PALETTE, type GerstnerWave } from "./config";
import { glslCoast, waveScale } from "./coast";

const _disp = new THREE.Vector3();

export function waveNumber(wavelength: number): number {
  return (Math.PI * 2) / wavelength;
}

export function waveOmega(k: number): number {
  return Math.sqrt(GRAVITY * k);
}

export function gerstnerDisplacement(
  x: number,
  z: number,
  time: number,
  waves: readonly GerstnerWave[] = HARBOUR_WAVES,
  target = _disp,
): THREE.Vector3 {
  let dx = 0;
  let dy = 0;
  let dz = 0;
  const scale = waveScale(x, z);

  for (const w of waves) {
    const len = Math.hypot(w.dirX, w.dirZ) || 1;
    const dirX = w.dirX / len;
    const dirZ = w.dirZ / len;
    const k = waveNumber(w.wavelength);
    const a = w.amplitude * scale;
    const q = w.steepness / Math.max(k * a * waves.length, 1e-4);
    const omega = waveOmega(k);
    const theta = k * (dirX * x + dirZ * z) - omega * time;
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    dx += q * a * dirX * cos;
    dz += q * a * dirZ * cos;
    dy += a * sin;
  }

  return target.set(dx, dy, dz);
}

export function gerstnerHeight(x: number, z: number, time: number): number {
  return gerstnerDisplacement(x, z, time).y;
}

function glslWaves(waves: readonly GerstnerWave[]): string {
  const n = waves.length;
  const dirs = waves.map((w) => {
    const len = Math.hypot(w.dirX, w.dirZ) || 1;
    return `vec2(${(w.dirX / len).toFixed(4)}, ${(w.dirZ / len).toFixed(4)})`;
  });
  const amps = waves.map((w) => w.amplitude.toFixed(4));
  const lams = waves.map((w) => w.wavelength.toFixed(4));
  const steep = waves.map((w) => w.steepness.toFixed(4));
  return `
const int WAVE_COUNT = ${n};
const vec2 WDIR[WAVE_COUNT] = vec2[](${dirs.join(", ")});
const float WAMP[WAVE_COUNT] = float[](${amps.join(", ")});
const float WLAM[WAVE_COUNT] = float[](${lams.join(", ")});
const float WSTP[WAVE_COUNT] = float[](${steep.join(", ")});
`;
}

const waterVertex = /* glsl */ `
uniform float uTime;
varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vCrest;
varying float vDepth;

${glslWaves(HARBOUR_WAVES)}
${glslCoast()}

void gerstner(vec2 xz, float t, out vec3 disp, out vec3 normal, out float crest) {
  disp = vec3(0.0);
  vec3 tangent = vec3(1.0, 0.0, 0.0);
  vec3 binormal = vec3(0.0, 0.0, 1.0);
  crest = 0.0;
  float scale = waveScaleAt(xz);
  vec2 travel = shoreTravel(xz);
  vec2 base = normalize(vec2(0.12, -1.0));
  float ang = atan(travel.y, travel.x) - atan(base.y, base.x);
  float cs = cos(ang);
  float sn = sin(ang);
  mat2 rot = mat2(cs, sn, -sn, cs);

  for (int i = 0; i < WAVE_COUNT; i++) {
    float k = 6.28318530718 / WLAM[i];
    float a = WAMP[i] * scale;
    float q = WSTP[i] / max(k * a * float(WAVE_COUNT), 0.0001);
    float omega = sqrt(9.81 * k);
    vec2 d = rot * WDIR[i];
    float theta = k * dot(d, xz) - omega * t;
    float c = cos(theta);
    float s = sin(theta);
    disp.x += q * a * d.x * c;
    disp.z += q * a * d.y * c;
    disp.y += a * s;
    crest += s * a;

    float wa = k * a;
    tangent += vec3(
      -q * d.x * d.x * wa * s,
      d.x * wa * c,
      -q * d.x * d.y * wa * s
    );
    binormal += vec3(
      -q * d.x * d.y * wa * s,
      d.y * wa * c,
      -q * d.y * d.y * wa * s
    );
  }
  normal = normalize(cross(binormal, tangent));
}

void main() {
  vec3 pos = position;
  vec3 disp;
  vec3 n;
  float crest;
  gerstner(pos.xz, uTime, disp, n, crest);
  vec3 displaced = pos + disp;
  vec4 world = modelMatrix * vec4(displaced, 1.0);
  vWorldPos = world.xyz;
  vNormal = normalize(mat3(modelMatrix) * n);
  vCrest = crest;
  vDepth = oceanDepthAt(world.xz);
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const waterFragment = /* glsl */ `
uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform vec3 uDeep;
uniform vec3 uShallow;
uniform vec3 uSand;
uniform vec3 uHorizon;
uniform vec3 uSky;
uniform float uTime;

varying vec3 vWorldPos;
varying vec3 vNormal;
varying float vCrest;
varying float vDepth;

void main() {
  vec3 n = normalize(vNormal);
  vec3 view = normalize(cameraPosition - vWorldPos);
  float ndv = max(dot(n, view), 0.0);
  float fresnel = pow(1.0 - ndv, 4.4);

  float depth = vDepth;
  vec3 scatter = mix(uSand, uShallow, smoothstep(0.2, 2.2, depth));
  scatter = mix(scatter, uDeep, smoothstep(3.5, 18.0, depth));
  vec3 abyss = uDeep * 0.55;
  scatter = mix(scatter, abyss, smoothstep(22.0, 55.0, depth));

  vec3 reflectCol = mix(uHorizon, uSky, clamp(n.y * 0.65 + 0.32, 0.0, 1.0));
  vec3 col = mix(scatter, reflectCol, fresnel * 0.86);

  vec3 halfV = normalize(uSunDir + view);
  float spec = pow(max(dot(n, halfV), 0.0), 180.0);
  float specWide = pow(max(dot(n, halfV), 0.0), 28.0);
  col += uSunColor * (spec * 1.35 + specWide * 0.1);

  float sss = pow(max(vCrest * 1.6, 0.0), 1.3) * (1.0 - ndv);
  col += uShallow * sss * 0.16;

  float foam = 1.0 - smoothstep(0.08, 1.15, depth);
  float chop = 0.5 + 0.5 * sin(vWorldPos.x * 1.4 + vWorldPos.z * 0.9 + uTime * 1.1);
  col = mix(col, vec3(0.90, 0.96, 0.95), foam * (0.35 + 0.3 * chop));

  float crestFoam = smoothstep(0.35, 0.85, vCrest);
  col = mix(col, vec3(0.93, 0.98, 0.97), crestFoam * 0.18);

  float dist = length(vWorldPos.xz - cameraPosition.xz);
  col = mix(col, uHorizon, smoothstep(420.0, 980.0, dist));

  gl_FragColor = vec4(col, 1.0);
}
`;

export function createWater(): THREE.Mesh {
  const geom = new THREE.PlaneGeometry(2200, 2200, 240, 240);
  geom.rotateX(-Math.PI / 2);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uSunDir: { value: new THREE.Vector3(0.35, 0.82, 0.45).normalize() },
      uSunColor: { value: new THREE.Color(0xfff1c8) },
      uDeep: { value: new THREE.Color(PALETTE.deep) },
      uShallow: { value: new THREE.Color(PALETTE.lagoon) },
      uSand: { value: new THREE.Color(PALETTE.sand) },
      uHorizon: { value: new THREE.Color(0xa9d7e0) },
      uSky: { value: new THREE.Color(0x6eb8e0) },
    },
    vertexShader: waterVertex,
    fragmentShader: waterFragment,
  });
  const mesh = new THREE.Mesh(geom, material);
  mesh.name = "water";
  mesh.frustumCulled = false;
  return mesh;
}

export function setWaterTime(water: THREE.Mesh, time: number): void {
  const mat = water.material as THREE.ShaderMaterial;
  mat.uniforms.uTime.value = time;
}

export function setWaterSun(water: THREE.Mesh, dir: THREE.Vector3): void {
  const mat = water.material as THREE.ShaderMaterial;
  (mat.uniforms.uSunDir.value as THREE.Vector3).copy(dir).normalize();
}
